'use client'

import {useEffect, useState} from 'react';
import {useSnapshot} from 'valtio';
import {useRouter, useSearchParams} from '@/utils/navigation';
import Cookies from 'js-cookie';
import getCompanies from '@/services/ProductService/getCompanies';
import store from '@/store';
import {Auth0Client} from '@auth0/auth0-spa-js';
import getSettings from "@/services/ThemeService/getSettings";
import {Setting} from "@/types/theme";
import {getLastActiveCompany} from "@/utils/lastVisitedPage";
import {EXPLICIT_LOGOUT_SESSION_KEY, isExplicitlyLoggedOut} from '@/utils/authSession';

// Initialize Auth0Client
let auth0Client: Auth0Client | null = null;

if (typeof window !== 'undefined') {
    auth0Client = new Auth0Client({
        domain: import.meta.env.VITE_AUTH_DOMAIN!,
        clientId: import.meta.env.VITE_AUTH_CLIENT_ID!,
        cacheLocation: 'localstorage',
        authorizationParams: {
            redirect_uri: window.location.origin,
            audience: 'https://auth.neatolabs.io/',
        },
    });
}

export const useCurrentUser = () => {

    const [token, setToken] = useState<string | null>(null);

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [user, setUser] = useState<any>(null);
    const { currentUser, status } = useSnapshot(store);

    const router = useRouter();
    const searchParams = useSearchParams();
    const upDateCompanySetting = async ()=> {

        if (!currentUser?.selectedCompany?.id) return
        const response: Setting[] = await getSettings();

        const machineValue = response?.find((setting) => setting.key === 'machine')?.value;
        store.companySetting.machine = typeof machineValue === 'string' ? machineValue : null;

        const profileVersionValue = response?.find((setting) => setting.key === 'profile_version')?.value;
        store.companySetting.profile_version = typeof profileVersionValue === 'string' ? profileVersionValue : 'v1';

        const erpIntegrationValue = response?.find((setting) => setting.key === 'erp_integration')?.value;
        store.companySetting.erp_integration = typeof erpIntegrationValue === 'string' && erpIntegrationValue.trim()
            ? erpIntegrationValue.trim()
            : null;

        const companyLogoValue = response?.find((setting) => setting.key === 'company_logo')?.value;
        store.companySetting.company_logo = typeof companyLogoValue === 'string' && companyLogoValue.trim()
            ? companyLogoValue.trim()
            : null;

        const brandColorValue = response?.find((setting) => setting.key === 'brand_color')?.value;
        store.companySetting.brand_color = typeof brandColorValue === 'string' && brandColorValue.trim()
            ? brandColorValue.trim()
            : null;

        //store.companySetting.machine = response.find((setting) => setting.key === 'machine')?.value || null;
        //console.log('companySettings------>', response)
    }

    const initializeAuth = async () => {
        if (isExplicitlyLoggedOut()) return;

        let tokenString: string;
        try {

            if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
                await auth0Client?.handleRedirectCallback();
                window.history.replaceState({}, document.title, window.location.pathname);
            }

            tokenString = await auth0Client?.getTokenSilently() || '';
            const loggedIn = await auth0Client?.isAuthenticated();
            if (!tokenString || !loggedIn) {
                await auth0Client?.loginWithRedirect();
            } else {
                const userData = await auth0Client?.getUser();
                setUser(userData);
                setIsAuthenticated(true);
                setToken(tokenString);
                //setIsLoading(false);
            }

        } catch (e:any) {
            console.log('token error', e);
            await auth0Client?.loginWithRedirect();
            //setIsLoading(false);
        } finally {
            setIsLoading(false);
        }

    };

    // TODO: REMOVE THIS ONE
    // Initialize and fetch authentication state
    const initializeAuth2 = async () => {
        setIsLoading(true);

        try {
            // Handle redirect callback once after returning from Auth0
            if (window.location.search.includes('code=') && window.location.search.includes('state=')) {
                await auth0Client?.handleRedirectCallback();
                //window.history.replaceState({}, document.title, window.location.pathname);
            }

            const loggedIn = await auth0Client?.isAuthenticated();

            if (!loggedIn) {
                await auth0Client?.loginWithRedirect();
                return;
            }

            const tokenString = await auth0Client?.getTokenSilently();
            const userData = await auth0Client?.getUser();

            setUser(userData);
            setIsAuthenticated(true);
            setToken(tokenString || '');
        } catch (e: any) {
            //console.log('token error', e);
        } finally {
            setIsLoading(false);
        }
    };



    // Set user data and companies in cookies and store
    const setCookie = async (user: any) => {


        let token = await auth0Client?.getTokenSilently() || '';

        Cookies.set('neatolabsToken', token);

        const companies = user['https://auth.neatolabs.com/companies'] || [];
        // Neatolabs staff (admin, support, maintenance) may enter any company.
        // A missing or non-boolean claim means a normal user, so older tokens behave as before.
        const hasPlatformAccess = user['https://auth.neatolabs.com/platform_access'] === true;
        if (!currentUser) {
            const allCompanies = await getCompanies(token);
            const companyList = hasPlatformAccess
                ? allCompanies
                : allCompanies.filter((company: any) =>
                    companies.includes(company.id)
                );

            const requestedStore = searchParams?.get('storeName') || sessionStorage.getItem('storeName') || null;
            const lastActiveCompany = requestedStore ? null : getLastActiveCompany(user.email);
            const matchedCompany = requestedStore
                ? companyList.find((company: any) => company.store_name === requestedStore)
                : companyList.find((company: any) => company.id === lastActiveCompany?.companyId) || null;

            if (matchedCompany) {
                await addStoreNameQuery(matchedCompany.store_name);
            } else if (companyList.length === 1 && !requestedStore) {
                await addStoreNameQuery(companyList[0].store_name);
            }

            const storeName = matchedCompany?.store_name
                || (companyList.length === 1 && !requestedStore ? companyList[0].store_name : null)
                || null;

            addStoreNameToLocalStorage(storeName);

            store.currentUser = {
                selectedCompany: matchedCompany || (companyList.length === 1 && !requestedStore ? companyList[0] : null),
                companies: companyList,
                name: user.name || '',
                nickname: user.nickname || '',
                email: user.email || '',
                first_name: user.given_name || '',
                last_name: user.family_name || '',
                full_name: user.name || '',
                email_verified: user.email_verified || false,
                picture: user.picture || '',
                updated_at: user.updated_at || '',
                roles: user['https://auth.neatolabs.com/roles'] || [],
                isAuthenticated: true,
                isMultiUser: hasPlatformAccess || companies.length > 1,
            };


            await upDateCompanySetting();
            store.status.authExpired = false;
        }
        store.loadingPage = false;
    };

    useEffect(() => {
        if (currentUser?.selectedCompany?.id) {
            upDateCompanySetting();
        }

    }, [currentUser?.selectedCompany]);


    // Your original useEffect logic is restored
    useEffect(() => {

        if (!isLoading && isAuthenticated && !currentUser) {
            store.loadingPage = "Please wait...";
        }
        if(!token && !isExplicitlyLoggedOut()) {
            initializeAuth();
        }
        if (token && user && !currentUser) {
            setCookie(user);
        }

        return () => {
            store.loadingPage = null;
        };
    }, [user, isLoading, isAuthenticated, status.authExpired]);



    const addStoreNameToLocalStorage = async (storeName: string | null) => {
        if (!storeName) return;

        const companyHistoryJson = localStorage.getItem('company_history');
        let companyHistory = companyHistoryJson ? JSON.parse(companyHistoryJson) : [];
        const company = currentUser?.companies?.find(
            (company: any) => company.store_name === storeName
        );
        if (!company) return;

        companyHistory = companyHistory.filter(
            (item: { id: string }) => item.id !== company.id
        );
        const updatedHistory = [{ ...company }, ...companyHistory].slice(0, 20);
        localStorage.setItem("company_history", JSON.stringify(updatedHistory));

        return updatedHistory;
    };



    const addStoreNameQuery = (storeName: string) => {
        const currentParams = new URLSearchParams(searchParams.toString());
        currentParams.set('storeName', storeName);
        router.replace(`?${currentParams.toString()}`);
        sessionStorage.setItem('storeName', storeName);
        addStoreNameToLocalStorage(storeName);
    };



    const handleLogOut = async () => {
        sessionStorage.setItem(EXPLICIT_LOGOUT_SESSION_KEY, 'true');
        store.currentUser = null;
        store.status.authExpired = false;
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        Cookies.remove('neatolabsToken');
        sessionStorage.removeItem('storeName');
        sessionStorage.removeItem('sso_redirect');

        try {
            await auth0Client?.logout({
                logoutParams: {
                    returnTo: window.location.origin,
                },
            });
        } catch (error) {
            console.error('Logout failed', error);
            window.location.assign('/_auth/login');
        }
    };



    return {
        user,
        setCurrentAuth: () => {},
        logout: handleLogOut,
        addStoreNameQuery,
    };
};
